import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import useArticleDetail from "#/admin/articles/hooks/useArticleDetail";
import BlockRenderer from "#/admin/articles/renderers/BlockRenderer";
import ArticleReviewsSummary from "#/admin/article-reviews/components/ArticleReviewsSummary";
import useArticleReviewRevision from "#/admin/articles/hooks/useArticleReviewRevision";
import useArticleReviewsList from "#/admin/article-reviews/hooks/useArticleReviewsList";
import useArticleRevisions from "#/admin/articles/hooks/useArticleRevisions";
import { formatDate } from "#/components/utls/time";
import { deleteArticle, requestArticleReview, rollbackArticleRevision } from "#/services/articles";

export default function AdminArticleDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const detailQuery = useArticleDetail(slug);
  const reviewRevisionMutation = useArticleReviewRevision();
  const reviewMutation = useMutation({
    mutationFn: async (articleId: string) => requestArticleReview(articleId),
  });
  const rollbackMutation = useMutation({
    mutationFn: async (params: { articleId: string; revisionId: string }) =>
      rollbackArticleRevision(params.articleId, params.revisionId),
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteArticle(id),
    onSuccess: () => navigate("/admin/content?tab=articles"),
  });
  const [pollUntil, setPollUntil] = useState<number | null>(null);

  const missingSlug = !slug;

  const articleTitle =
    detailQuery.data?.article?.title || detailQuery.data?.article?.slug || "Article";
  const articleId = detailQuery.data?.article?.id;

  const reviewsQuery = useArticleReviewsList(
    { articleId: articleId ?? undefined },
    { enabled: Boolean(articleId) }
  );
  const revisionsQuery = useArticleRevisions(articleId);

  const actionableReviewCount = useMemo(() => {
    const items = Array.isArray(reviewsQuery.data) ? reviewsQuery.data : [];
    return items.filter(
      (review) => review.status === "pending" || review.status === "warn"
    ).length;
  }, [reviewsQuery.data]);

  const shouldTriggerRevision = actionableReviewCount > 0;

  useEffect(() => {
    if (!pollUntil) return;
    const intervalId = window.setInterval(() => {
      detailQuery.refetch();
      reviewsQuery.refetch();
      revisionsQuery.refetch();
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [pollUntil, detailQuery, reviewsQuery, revisionsQuery]);

  useEffect(() => {
    if (!pollUntil) return;
    const timeoutId = window.setTimeout(() => {
      setPollUntil(null);
    }, Math.max(0, pollUntil - Date.now()));
    return () => window.clearTimeout(timeoutId);
  }, [pollUntil]);

  if (missingSlug) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-6">
          <Typography variant="h4" className="text-terracotta">
            Article not found
          </Typography>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-brown-500">
              Articles / {articleTitle}
            </Typography>
            <Typography variant="h2" className="text-brown-900">
              {articleTitle}
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <ActionButton
              onClick={async () => {
                if (!articleId || pollUntil) return;
                if (shouldTriggerRevision) {
                  await reviewRevisionMutation.mutateAsync(articleId);
                } else {
                  await reviewMutation.mutateAsync(articleId);
                }
                setPollUntil(Date.now() + 180_000);
              }}
              isLoading={reviewRevisionMutation.isPending || reviewMutation.isPending}
              disabled={!articleId || Boolean(pollUntil)}
            >
              {pollUntil
                ? "Processing…"
                : shouldTriggerRevision
                  ? "Trigger revision"
                  : "Review this article"}
            </ActionButton>
            <ActionButton
              tone="secondary"
              onClick={() => navigate("/admin/content?tab=articles")}
            >
              Back to articles
            </ActionButton>
            <ActionButton
              tone="danger"
              isLoading={deleteMutation.isPending}
              disabled={!articleId || deleteMutation.isPending}
              onClick={async () => {
                if (!articleId) return;
                const confirmed = window.confirm(
                  `Permanently delete "${articleTitle}"?\n\nThis removes the article, all blocks, revisions, and reviews. The medical concept and outline will be kept so the article can be regenerated.\n\nThis cannot be undone.`
                );
                if (!confirmed) return;
                await deleteMutation.mutateAsync(articleId);
              }}
            >
              Delete article
            </ActionButton>
          </div>
        </div>

        {pollUntil ? (
          <Typography variant="body2" className="text-brown-600">
            Refreshing article content while updates process…
          </Typography>
        ) : null}

        {articleId ? (
          <div className="rounded-lg border border-beige-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <Typography variant="h4" className="text-brown-900">
                Revision history
              </Typography>
              <ActionButton
                size="sm"
                tone="secondary"
                onClick={() => revisionsQuery.refetch()}
                isLoading={revisionsQuery.isFetching}
              >
                Refresh
              </ActionButton>
            </div>
            {revisionsQuery.isLoading ? (
              <Typography variant="body2" className="text-brown-600">
                Loading revisions…
              </Typography>
            ) : revisionsQuery.isError ? (
              <Typography variant="body2" className="text-terracotta">
                {revisionsQuery.error instanceof Error
                  ? revisionsQuery.error.message
                  : "Unable to load revision history."}
              </Typography>
            ) : revisionsQuery.data?.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-cream-50 text-brown-700">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Rev</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Created</th>
                      <th className="px-3 py-2 font-semibold">Message</th>
                      <th className="px-3 py-2 font-semibold">Revision ID</th>
                      <th className="px-3 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-beige-100">
                    {revisionsQuery.data.map((revision) => (
                      <tr key={revision.id}>
                        <td className="px-3 py-2 text-brown-900">r{revision.rev}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center rounded-full bg-beige-100 px-2 py-1 text-xs font-semibold text-brown-700">
                            {revision.isPublished ? "Published" : "Historical"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-brown-700">
                          {formatDate(revision.createdAt)}
                        </td>
                        <td className="px-3 py-2 text-brown-700">
                          {revision.message?.trim() || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-brown-600">
                          {revision.id}
                        </td>
                        <td className="px-3 py-2">
                          {revision.isPublished ? (
                            <span className="text-xs text-brown-500">Current</span>
                          ) : (
                            <ActionButton
                              size="sm"
                              tone="secondary"
                              isLoading={rollbackMutation.isPending}
                              onClick={async () => {
                                if (!articleId) return;
                                const confirmed = window.confirm(
                                  `Rollback article to revision r${revision.rev}? This will create a new published revision.`
                                );
                                if (!confirmed) return;
                                await rollbackMutation.mutateAsync({
                                  articleId,
                                  revisionId: revision.id,
                                });
                                setPollUntil(Date.now() + 30_000);
                                await Promise.all([
                                  detailQuery.refetch(),
                                  reviewsQuery.refetch(),
                                  revisionsQuery.refetch(),
                                ]);
                              }}
                            >
                              Roll back to this
                            </ActionButton>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Typography variant="body2" className="text-brown-600">
                No revisions yet.
              </Typography>
            )}
          </div>
        ) : null}

        {articleId ? <ArticleReviewsSummary articleId={articleId} /> : null}

        {detailQuery.isLoading ? (
          <Typography variant="body2" className="text-brown-600">
            Loading article…
          </Typography>
        ) : detailQuery.isError ? (
          <Typography variant="body2" className="text-terracotta">
            {detailQuery.error instanceof Error
              ? detailQuery.error.message
              : "Unable to load article."}
          </Typography>
        ) : detailQuery.data?.article ? (
          <BlockRenderer
            payload={detailQuery.data}
            onAssetReviewSubmitted={() => setPollUntil(Date.now() + 180_000)}
          />
        ) : (
          <div className="rounded-lg border border-beige-200 bg-cream-50 p-4">
            <Typography variant="body2" className="text-brown-700">
              This article does not include markdown content to render. Raw data
              is shown below.
            </Typography>
            <pre className="mt-3 max-h-[60vh] overflow-auto whitespace-pre-wrap text-xs text-brown-700">
              {JSON.stringify(detailQuery.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </AdminOnly>
  );
}
